import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  Output,
  EventEmitter,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  ReactiveFormsModule,
  FormControl,
} from '@angular/forms';
import { RequestsService } from '@api/services/requests.service';
import { IconComponent } from '@ui/icon/icon.component';
import { InputComponent } from '@ui/input/input.component';
import { switchMap } from 'rxjs/operators';
import { filter, tap } from 'rxjs/operators';
import { debounceTime } from 'rxjs/operators';
import { ButtonComponent } from '@ui/button/button.component';

/** Velden die bij "Gebruik deze referentie" vanuit bestaande aanvraag worden gevuld. */
const CLIENT_FIELDS_FROM_EXISTING_REF = [
  'first_name',
  'last_name',
  'street',
  'housenumber',
  'housenumber_addon',
  'postal_code',
  'city',
  'date_of_birth',
  'bsn',
  'phone',
  'email',
] as const;

@Component({
  selector: 'app-request-wmo-reference-step',
  standalone: true,
  imports: [
    CommonModule,
    InputComponent,
    ReactiveFormsModule,
    IconComponent,
    ButtonComponent,
  ],
  template: `
    <div class="space-y-6">
      <div class="space-y-4">
        <h2 class="text-lg font-medium flex items-center gap-2">
          <ui-icon name="document-text" class="w-5 h-5"></ui-icon>
          WMO referentie
        </h2>

        <div class="grid grid-cols-2 gap-4">
          <div class="relative">
            <ui-input
              label="WMO referentie"
              [formControl]="wmoReferenceControl"
              [required]="true"
              [readonly]="isEditMode || useExistingReference()"
              placeholder="WMO referentie"
            ></ui-input>
            @if(isCheckingWmoReference()) {
            <div class="absolute right-2 top-8">
              <ui-icon name="spinner" class="animate-spin"></ui-icon>
            </div>
            }
          </div>
        </div>

        @if(existingRequestDetails() && !useExistingReference() &&
        requestForm.get('wmo_reference')?.value) {
        <div
          class="mt-4 p-4 bg-warning-50 border border-red-500 rounded-lg text-sm text-warning-800"
        >
          <div class="flex flex-col gap-2">
            <p class="text-warning-800">
              Deze WMO referentie bestaat al voor:
            </p>
            <div class="flex items-center gap-2">
              <ui-icon
                name="person"
                class="w-5 h-5 text-warning-600"
              ></ui-icon>
              <span class="font-medium">
                {{ existingRequestDetails()?.first_name }}
                {{ existingRequestDetails()?.last_name }}
              </span>
            </div>
            <div class="flex items-center gap-2" style="align-items: top;">
              <ui-icon
                name="map-pin"
                class="w-5 h-5 text-warning-600"
              ></ui-icon>
              <span
                >{{ existingRequestDetails()?.street }}
                {{ existingRequestDetails()?.housenumber }}
                {{ existingRequestDetails()?.housenumber_addon }}
                <br />
                {{ existingRequestDetails()?.postal_code }}
                {{ existingRequestDetails()?.city }}</span
              >
            </div>
            <div class="flex gap-4 mt-4">
              <ui-button
                type="button"
                variant="primary"
                (click)="setUseExistingReference(true)"
              >
                Gebruik deze referentie
              </ui-button>
              <button
                type="button"
                class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                (click)="setUseExistingReference(false)"
              >
                Andere referentie invoeren
              </button>
            </div>
          </div>
        </div>
        }
      </div>
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestWmoReferenceStepComponent {
  private requestsService = inject(RequestsService);
  private destroyRef = inject(DestroyRef);

  @Input() requestForm!: FormGroup;
  @Input() isEditMode = false;
  /** Snapshot van de WMO waarvoor clientgegevens zijn overgenomen (van parent; blijft na remount). */
  @Input() importedClientWmoRef: string | null = null;
  @Output() importCommitted = new EventEmitter<string>();
  @Output() importCleared = new EventEmitter<void>();


  isCheckingWmoReference = signal<boolean>(false);
  existingRequestDetails = signal<any | null>(null);
  useExistingReference = signal<boolean>(false);
  wmoReferenceError = signal<string | undefined>(undefined);

  get wmoReferenceControl(): FormControl {
    return this.requestForm.get('wmo_reference')?.get('wmo_reference') as FormControl;
  }



  ngOnInit() {
    this.wmoReferenceControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.tryClearImportedWhenWmoChanged());

    this.wmoReferenceControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => {
        this.wmoReferenceControl.setErrors({ invalid: true });
      }),
      debounceTime(500),
      filter(
        (value): value is string =>
          !!value && value.length > 0 && !this.isEditMode
      ),
      tap(() => {
        this.isCheckingWmoReference.set(true);
        this.existingRequestDetails.set(null);
        this.useExistingReference.set(false);
        this.wmoReferenceControl.setErrors({ invalid: true });
      }),
      switchMap((value) => this.requestsService.checkWmoReference(value))
    )
    .subscribe({
      next: (response) => {
        this.isCheckingWmoReference.set(false);
        if (response.exists) {
          let message = response.message;
          if (response.request) {
            this.existingRequestDetails.set(response.request.details);
            message = `Deze WMO referentie bestaat al binnen deze gemeente voor ${
              response.request.details.first_name
            } ${response.request.details.last_name} met status ${
              response.request.statuses[response.request.statuses.length - 1]
                .status
            }`;
          }
          this.wmoReferenceError.set(message);
          this.wmoReferenceControl.setErrors({ exists: true });
        } else {
          this.wmoReferenceError.set(undefined);
          this.existingRequestDetails.set(null);
          this.useExistingReference.set(false);
          this.wmoReferenceControl.setErrors(null);
        }
      },
      error: () => {
        this.isCheckingWmoReference.set(false);
      },
    });
  }

  private trimWmo(value: unknown): string {
    return (value ?? '').toString().trim();
  }

  /** Clientvelden leeg die via "Gebruik deze referentie" waren gezet. */
  private clearClientFieldsFromImport(): void {
    for (const key of CLIENT_FIELDS_FROM_EXISTING_REF) {
      this.requestForm.get(key)?.setValue('');
    }
  }

  /**
   * Als er een import-snapshot is (parent) en de WMO wijkt af → gegevens wissen
   * (ook na stap 3 → terug naar stap 2, want snapshot zit op de modal).
   */
  private tryClearImportedWhenWmoChanged(): void {
    const snap = this.trimWmo(this.importedClientWmoRef);
    if (!snap) return;
    const v = this.trimWmo(this.wmoReferenceControl.value);
    if (v !== snap) {
      this.clearClientFieldsFromImport();
      this.useExistingReference.set(false);
      this.importCleared.emit();
    }
  }

  setUseExistingReference = (value: boolean) => {
    this.useExistingReference.set(value);
    if (value) {
      this.wmoReferenceControl.setErrors(null);
      this.requestForm.get('first_name')?.setValue(this.existingRequestDetails()?.first_name);
      this.requestForm.get('last_name')?.setValue(this.existingRequestDetails()?.last_name);
      this.requestForm.get('street')?.setValue(this.existingRequestDetails()?.street);
      this.requestForm.get('housenumber')?.setValue(this.existingRequestDetails()?.housenumber);
      this.requestForm.get('housenumber_addon')?.setValue(this.existingRequestDetails()?.housenumber_addon);
      this.requestForm.get('postal_code')?.setValue(this.existingRequestDetails()?.postal_code);
      this.requestForm.get('city')?.setValue(this.existingRequestDetails()?.city);
      this.requestForm.get('date_of_birth')?.setValue(this.existingRequestDetails()?.date_of_birth);
      this.requestForm.get('bsn')?.setValue(this.existingRequestDetails()?.bsn);
      this.requestForm.get('phone')?.setValue(this.existingRequestDetails()?.phone);
      this.requestForm.get('email')?.setValue(this.existingRequestDetails()?.email);
      this.importCommitted.emit(this.trimWmo(this.wmoReferenceControl.value));
    } else if (this.trimWmo(this.importedClientWmoRef)) {
      this.clearClientFieldsFromImport();
      this.importCleared.emit();
    }
  };
}
